import { useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { DispatchPanel } from "@/components/Dispatch/DispatchPopover";
import {
  dispatchPagePath,
  dispatchPanelPath,
  followedPanelPath,
  PANEL_QUERY_PARAM,
  panelFromParam,
} from "./dispatchPanelRoute";
import type { DispatchTableRow } from "./dispatchTableRow";
import { rowFeedId } from "./dispatchTableRow";

/**
 * The popover's home in the URL: which driver and channel are open, on which tab, and how to move between them.
 *
 * The route is `/dispatch/:membershipId/channel/:feedId?panel=`, so a colleague can be sent the link and a reload
 * changes nothing; `?view=` rides along on every navigation so the panel never loses the view. H and C are toggles:
 * opening the tab that is already open on the same driver closes the popover instead. An open tab follows the
 * selection: `followRow` moves it to the driver the highlight lands on, on the same tab, replacing the history entry
 * so Back still leaves the popover rather than retracing every step through the table. `openPanel` says whether it
 * opened the tab, so the page can report an opening and stay quiet about a toggle that closed it.
 *
 * @param workspaceId - The workspace the page is on; navigation is a no-op without one.
 * @param viewId - The `?view=` value in force, or `null` when the URL names none.
 * @param onClosed - Runs once the popover's route is left, so the page can take the keyboard back.
 */
export function useDispatchPanelRoute(
  workspaceId: string | undefined,
  viewId: string | null,
  onClosed?: () => void,
): {
  openFeedId: string | undefined;
  openMembershipId: string | undefined;
  panel: DispatchPanel;
  /** Opens `next` on `row`'s driver; false when it closed the popover instead, or the row has no channel to open. */
  openPanel: (next: DispatchPanel, row: DispatchTableRow) => boolean;
  closePanel: () => void;
  changePanel: (next: DispatchPanel) => void;
  followRow: (row: DispatchTableRow) => void;
} {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { feedId: openFeedId, workspaceMembershipId: openMembershipId } = useParams();
  const panel = panelFromParam(searchParams.get(PANEL_QUERY_PARAM));

  const closePanel = useCallback(() => {
    if (workspaceId && openFeedId) navigate(dispatchPagePath(workspaceId, viewId));
    onClosed?.();
  }, [workspaceId, openFeedId, viewId, navigate, onClosed]);

  const openPanel = useCallback(
    (next: DispatchPanel, row: DispatchTableRow): boolean => {
      const feedId = rowFeedId(row);
      if (!feedId || !workspaceId) return false;
      if (openFeedId === feedId && panel === next) {
        closePanel();
        return false;
      }
      navigate(dispatchPanelPath(workspaceId, row.driver.workspaceMembershipId, feedId, next, viewId));
      return true;
    },
    [workspaceId, openFeedId, panel, viewId, closePanel, navigate],
  );

  const changePanel = useCallback(
    (next: DispatchPanel) => {
      if (!workspaceId || !openMembershipId || !openFeedId) return;
      navigate(dispatchPanelPath(workspaceId, openMembershipId, openFeedId, next, viewId), { replace: true });
    },
    [workspaceId, openMembershipId, openFeedId, viewId, navigate],
  );

  const followRow = useCallback(
    (row: DispatchTableRow) => {
      if (!workspaceId || !openMembershipId) return;
      const path = followedPanelPath(workspaceId, { membershipId: openMembershipId, panel }, row, viewId);
      if (path) navigate(path, { replace: true });
    },
    [workspaceId, openMembershipId, panel, viewId, navigate],
  );

  return { openFeedId, openMembershipId, panel, openPanel, closePanel, changePanel, followRow };
}
